/**
 * A LINK COPIED OUT OF AN OLD EMAIL LOSES THAT EMAIL'S TAGS (2026-09-22).
 *
 * Justin Bradford, FNO 68759: the View Community button and the blue bar's "view all"
 * both pointed at the community page WITH last send's tags still on it —
 *
 *   …/valencia-series-ii-at-club-district?utmcampaign=fnolenmmarketcca&utmterm=market
 *   &utmcontent=locorrection-riverstone25tsrjuncs003tmp20260911phrnan&utmmedium=email
 *   &utmsource=mc&utmcampaign=&utmcontent=&utmterm=&subkey=00Q1Q000014tmlmUAA
 *
 * — because the requester opened the 9/11 eblast (or its view-as-web-page preview),
 * right-clicked the button and copied the link, and SFMC had rewritten that href at
 * send time. So the new email would report its clicks under the OLD campaign and file
 * name, and carry ONE recipient's Salesforce subscriber key for everyone who clicked.
 *
 * Jack: "can the app just scrub that link … in the app itself?" — yes. Every URL box
 * runs its value through `scrubTracking` on change: the tracking parameters go, the
 * rest of the query survives (an Eventbrite `?aff=`, a Zoom `?pwd=`, a `#join-interest-
 * list` hash), and a value with nothing to strip is returned UNCHANGED, character for
 * character, so typing is never fought. The box says what it did in one grey line.
 *
 * What counts as tracking — a deliberately short list, because a false strip is a
 * dead link nobody notices until the send report:
 *   · utm_* and SFMC's flattened utm* (utmcampaign, utmterm, utmcontent, utmmedium, utmsource)
 *   · subkey — the recipient's subscriber key
 *   · sfmc_* — Marketing Cloud's own tracking keys
 *   · mc_cid / mc_eid — Mailchimp; gclid / dclid / fbclid / msclkid / _ga / _gl — ad and analytics click ids
 */
const TRACKING = /^(utm[_-][a-z0-9_]+|utm(source|medium|campaign|term|content|id)|subkey|sfmc_[a-z0-9_]*|mc_cid|mc_eid|gclid|dclid|fbclid|msclkid|_ga|_gl)$/i;

export interface Scrubbed {
  /** the link with tracking removed — identical to the input when nothing was removed */
  url: string;
  /** the parameter names that were removed, in order, de-duplicated */
  removed: string[];
}

export function isTrackingParam(name: string): boolean {
  return TRACKING.test(String(name || "").trim());
}

export function scrubTracking(raw: unknown): Scrubbed {
  const v = String(raw ?? "");
  const q = v.indexOf("?");
  if (q < 0) return { url: v, removed: [] };
  /* a mailto: carries ?subject= — never touched (its "query" is the email's subject) */
  if (/^\s*mailto:/i.test(v)) return { url: v, removed: [] };
  const hashAt = v.indexOf("#", q);
  const base = v.slice(0, q);
  const query = hashAt < 0 ? v.slice(q + 1) : v.slice(q + 1, hashAt);
  const hash = hashAt < 0 ? "" : v.slice(hashAt);
  const keep: string[] = [];
  const removed: string[] = [];
  for (const part of query.split("&")) {
    if (part === "") { keep.push(part); continue; }
    const eq = part.indexOf("=");
    const name = eq < 0 ? part : part.slice(0, eq);
    if (isTrackingParam(name)) {
      if (removed.indexOf(name) < 0) removed.push(name);
    } else keep.push(part);
  }
  if (!removed.length) return { url: v, removed: [] };
  const rest = keep.filter((p) => p !== "").join("&");
  return { url: base + (rest ? "?" + rest : "") + hash, removed };
}

/** true when `scrubTracking` would change this value */
export const hasTracking = (raw: unknown): boolean => scrubTracking(raw).removed.length > 0;

/** the one grey line the box shows after a scrub */
export function scrubNote(removed: string[]): string {
  if (!removed.length) return "";
  const sub = removed.some((n) => /^subkey$/i.test(n));
  return sub
    ? "Removed tracking tags and a subscriber ID from a previous send — the clean page link is kept."
    : "Removed tracking tags from a previous send — the clean page link is kept.";
}
